import {Component, Element, h, Listen, Prop} from '@stencil/core';
import type { PaginationEvent } from '../../result-page/search-tab/PaginationEvent';
import type { SearchResultClickEvent } from '../../result-page/search-tab/SearchResultClickEvent';
import type { ViewChangeEvent } from '../../result-page/search-tab/ViewChangeEvent';
import type { TabAnalyticsEvent } from '../../result-page/tabs-header/TabAnalyticsEvent';
import { getSiteSearchCategory, pushToDataLayer} from '../util';
import type {AnalyticsGenericEvent} from '../../../utils/analytics/AnalyticsGenericEvent';
import type {RelevancyWidgetClickEvent} from '../../widgets/relevancy-score/relevancyWidgetClickEvent';
import type {RelevancyWidgetFoldEvent} from '../../widgets/relevancy-score/relevancyWidgetFoldEvent';
import type {CtaWidgetClickEvent} from '../../widgets/cta-widget/cta-widget';
import {Strategies, StrategiesFactory} from '../../strategies/Strategies';
import {TransformEventsNamesStrategyService} from '../../strategies/TransformEventsNamesStrategyService';
import {TRANSFORM_EVENTS_NAMES} from '../../strategies/utils/variables';
import type { RbcViewItemListEvent } from '../../result-page/search-tab/RbcViewItemListEvent';


@Component({
  tag: 'uiaas-search-analytics-reporting-svs',
})
export class UiaasSearchAnalyticsReportingSvsComponent {

    @Element() el;
    private _strategies: Strategies;

    @Prop() readonly env?: string;

    @Prop() readonly language?: string;
    @Prop() readonly businessUnit?: string;
    @Prop() readonly articleTitle?: string;
    @Prop() readonly digitalPlatform?: string;
    @Prop() readonly digitalPlatformCountry?: string;
    @Prop() readonly fromWebView?: boolean;
    @Prop() readonly pageTopCategory?: string;
    @Prop() readonly pageCategory?: string;
    @Prop() readonly template?: string;
    @Prop() readonly pageTitle?: string;

    private EVENT_LABEL_SEPARATOR = '###';

    private transformEventsNames: TransformEventsNamesStrategyService;
    private prepareEventData = (data) => this.transformEventsNames?.transformEventsNames(data);
    get strategies(): Strategies {
        return this._strategies;
    }

    set strategies(value: Strategies) {
        this._strategies = value;
    }

    componentWillLoad() {
        this.strategies = StrategiesFactory.createStrategy(this.el);
        this.transformEventsNames = this.strategies.for(TRANSFORM_EVENTS_NAMES);
    }
    @Listen('search:tabClick')
    tabClickEventHandler(event: TabAnalyticsEvent & CustomEvent) {
        const action = 'click';
        const label = [
          'tabName:' + event.detail.tabName,
          'hits:' + event.detail.tabCount,
          'position:' + event.detail.position,
          'isHidden:' + event.detail.isHidden,
        ].join(this.EVENT_LABEL_SEPARATOR);

        this.sendEventToAnalytics(event.detail.category, action, label, event.detail.destinationURL, event.detail.keyword);
    }

    @Listen('search:moreTabsClick')
    tabShowMoreEventHandler() {
        const category = 'tab-nav';
        const action = 'unfold';
        const label = 'more-button';

        this.sendEventToAnalytics(category, action, label);
    }

    @Listen('search:viewTypeChangeClickEvent')
    viewTypeChangeEventHandler(event: ViewChangeEvent & CustomEvent) {
        const category = 'viewType';
        const action = 'click';
        const track = event.detail;
        const label = [
          'viewType:' + track.viewType,
          'initialViewType:' + track.initialViewType,
          'tabName:' + getSiteSearchCategory(track.tabName, track.appName),
        ].join(this.EVENT_LABEL_SEPARATOR);

        this.sendEventToAnalytics(category, action, label);
    }

    @Listen('search:rbcViewTypeChangeClickEvent')
    rbcViewTypeChangeEventHandler(event: RbcViewItemListEvent & CustomEvent) {
        pushToDataLayer(event.detail);
    }

    @Listen('search:PaginationLoadMoreClick')
    loadMoreClickEventHandler(event: PaginationEvent & CustomEvent) {
        const category = 'pagination';
        const action = 'loadMore';
        const track = event.detail;
        const label = [
          'tabName:' + track.tabName,
          'perPage:' + track.perPage,
          'numberOfAlreadyDisplayedResults:' + track.numberOfAlreadyDisplayedResults,
        ].join(this.EVENT_LABEL_SEPARATOR);

        this.sendEventToAnalytics(category, action, label);
    }


    @Listen('search:relevancyWidgetFold')
    relevancyWidgetFoldEventHandlere(e:RelevancyWidgetFoldEvent & CustomEvent) {
        const category = 'widget-relevancy-score';
        const action = 'fold';
        const payload = e.detail;
        const label = [
          'name:' + payload.name,
          'icon:' + payload.icon,
          'tab:' + payload.tab,
        ].join(this.EVENT_LABEL_SEPARATOR);
        this.sendEventToAnalytics(category, action, label);
    }

    @Listen('search:relevancyWidgetUnfold')
    relevancyWidgetFoldUnfoldHandler(e:RelevancyWidgetFoldEvent & CustomEvent) {
        const category = 'widget-relevancy-score';
        const action = 'unfold';
        const payload = e.detail;
        const label = [
          'name:' + payload.name,
          'icon:' + payload.icon,
          'tab:' + payload.tab,
        ].join(this.EVENT_LABEL_SEPARATOR);
        this.sendEventToAnalytics(category, action, label);
    }

  @Listen('search:ctaWidgetEvent')
    ctaWidgetEventHandler({detail}: AnalyticsGenericEvent & CustomEvent<CtaWidgetClickEvent>) {

        const {category, tabName, redirectionUrl, clickType} = detail || {};

        const action = 'click';

        const label = [
          'tab:' + tabName,
          'clickType:' + clickType,
          redirectionUrl ? 'openUrl:' + redirectionUrl : ''
        ].join(this.EVENT_LABEL_SEPARATOR);

        this.sendEventToAnalytics(category, action, label);
    }

    @Listen('search:relevancyWidgetClick')
  relevancyWidgetClickEventHandler(e:RelevancyWidgetClickEvent & CustomEvent) {
      const category = 'widget-relevancy-score';
      const action = 'click';
      const payload = e.detail;
      const label = [
        'clickedType:' + payload.clickedType,
        'name:' + payload.name,
        'state:' + payload.state,
        'icon:' + payload.icon,
        'tab:' + payload.tab,
      ].join('###');
      this.sendEventToAnalytics(category, action, label);
  }

    @Listen('search:widgetEvent')
    widgetsEventHandler(event: AnalyticsGenericEvent & CustomEvent) {
        const track = event.detail;
        const category = track.category;
        const action = track.action;
        const label = track.label;

        this.sendEventToAnalytics(category, action, label);
    }


    @Listen('search:widgetEvent')
    sendWidgetClickEvent(e:CustomEvent) {
        if (/^uiaas-search-substitution-tool$/i.test(e.detail.widget)) {
          const category = 'widget-cta-substitution-tool';
          const action = 'click';
          const label = [
            e.detail.tab ? ('tab:' + e.detail.tab) : '',
            'clickType:' + e.detail.clickType, //right, left, keyboard
            e.detail.redirectionUrl ? 'redirectionUrl:' + e.detail.redirectionUrl : ''
          ].filter(item=>!!item).join(this.EVENT_LABEL_SEPARATOR);

          this.sendEventToAnalytics(category, action, label);
        }
    }

    @Listen('search:autocorrectquery', {target: 'window'})
    sendCancelAutocorrectionEvent(e:CustomEvent) {
        const { misspelledKeyword, suggestion:autocorrectedKeyword } = e.detail;

        this.sendEventToAnalytics(
          'misspelling',
          'click',
          [
            'misspelledKeyword:' + misspelledKeyword,
            'autocorrectedKeyword:' + autocorrectedKeyword
          ].join(this.EVENT_LABEL_SEPARATOR)
        );
    }

    @Listen('sidePanel:SearchResultClickEvent')
    sidePanelResultClickEventHandler(event: SearchResultClickEvent & CustomEvent) {
        const category = 'search-result-card';
        const action = event.detail.actionType;
        const label = [
          'clickedArea:' + event.detail.clickedArea,
          'tab:' + event.detail.tabName,
          'position:' + event.detail.searchResultPosition,
          'clickType:' + event.detail.clickType,
          'actionType:' + event.detail.actionType,
          'commStatus:' + event.detail.commStatus,
        ].join(this.EVENT_LABEL_SEPARATOR);;

        this.sendEventToAnalytics(category, action, label);
    }

    private sendEventToAnalytics(
      category: String,
      action: String,
      label: String,
      destinationURL?: String,
      siteSearchKeyword?: String
    ) {
        if (category !== undefined && action !== undefined && label !== undefined) {
          pushToDataLayer(
            this.prepareEventData({
              event: 'OnSiteSearch',
              site_search_category: category,
              site_search_action: action,
              site_search_label: label,
              site_search_keyword: siteSearchKeyword,
              destinationURL: destinationURL,
            })
          );

        }
    }

    render() {
        return <slot />;
    }
}
